import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import { Link } from "react-router-dom";

const HANDS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4557cc87-798c-47bd-b347-d002eb8baf36/images/9607f2d9374d64d9ef140b0cd80f9c96c49a7e27998882fda1435ed6b902b2b7.png";

const ASKS = [
  "More published, anonymised family court judgments — so that decisions are visible and can be reasoned about by the public who fund them.",
  "Independent scrutiny of local authority decision-making, separate from the local authorities themselves.",
  "A statutory right to clear, plain-English information for every parent at the moment they enter the system.",
  "Better data on outcomes for children removed from their birth families — long-term, honest, and published.",
  "Robust support for kinship carers — the family members who step forward, often overnight, and are then frequently forgotten by the system.",
];

function EssaySidebar() {
  return (
    <aside className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start">
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        Essay
      </div>
      <div className="mt-3 font-accent text-sm text-ink-muted">A long read</div>
      <div className="mt-6 h-px w-12 bg-ember/60" />
      <p className="mt-6 text-sm text-ink-muted leading-relaxed font-light">
        Decisions that separate children from their parents are among the most
        serious any state can make. They deserve scrutiny that matches that
        weight.
      </p>
      <LastReviewed path="/campaign" className="mt-6" />
    </aside>
  );
}

function OpeningParas() {
  return (
    <div className="prose-editorial space-y-6 text-lg md:text-xl text-ink-muted leading-[1.8] font-light">
      <p className="text-2xl md:text-3xl font-serif text-ink leading-snug text-balance">
        Decisions that separate children from their parents are among the most serious any state can make — yet in England and Wales they are made largely behind closed doors.
      </p>
      <p>
        Family court hearings are usually held in private. Many judgments are
        never published. Families frequently come away without ever fully
        understanding why the most important decision of their lives was reached.
      </p>
      <p>This matters all the more given the scale of the system.</p>
    </div>
  );
}

function ScaleStat() {
  return (
    <div className="my-16 border-y border-rule py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
      <div className="md:col-span-5">
        <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
          The scale
        </div>
        <div className="mt-4 font-serif text-6xl md:text-7xl lg:text-8xl text-river tracking-tighter leading-none">
          £15.5bn
        </div>
        <div className="mt-3 text-sm text-ink-muted font-accent">
          Spent by councils on children&rsquo;s social care in England, 2024–25.
        </div>
      </div>
      <p className="md:col-span-7 text-lg md:text-xl text-ink-muted leading-relaxed font-light">
        Children&rsquo;s social care in England is now an enormous undertaking. Local authorities sit at the centre of it — they assess families, bring cases to court, commission the placements children are moved into, and hold the budgets that fund the wider ecosystem of services around them. When the same body holds so many roles at once, fairness depends on real transparency and genuine independent scrutiny.
      </p>
    </div>
  );
}

function BeliefSection() {
  return (
    <>
      <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight tracking-tight mt-12">
        What we believe
      </h2>
      <p>
        We want to be clear about something important: children are sometimes at
        genuine risk, and the people working in this field often face
        heartbreaking, impossible decisions. We do not believe every
        intervention is wrong, and we do not stand against child protection.
      </p>
      <blockquote className="border-l-4 border-ember pl-6 md:pl-8 py-2 my-10">
        <p className="font-serif text-2xl md:text-3xl text-ink leading-snug not-italic">
          We stand for a system that is open enough to be trusted — one in which families understand what is happening to them, can challenge decisions that are wrong, and are treated with dignity throughout.
        </p>
      </blockquote>
    </>
  );
}

function AsksList() {
  return (
    <>
      <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight tracking-tight mt-12">
        What we are asking for
      </h2>
      <ul className="space-y-4 text-base md:text-lg text-ink-muted">
        {ASKS.map((item) => (
          <li key={item.slice(0, 40)} className="flex gap-4">
            <span className="font-serif text-ember text-2xl leading-none mt-1">
              —
            </span>
            <span className="leading-relaxed font-light">{item}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function StandWithUs() {
  return (
    <div className="mt-16 p-8 md:p-12 bg-river text-linen">
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        Stand with us
      </div>
      <h3 className="font-serif text-3xl md:text-4xl mt-4 leading-tight tracking-tight text-balance">
        If this rings true to you, we&rsquo;d be glad to hear from you.
      </h3>
      <p className="mt-5 text-linen/80 leading-relaxed max-w-2xl font-light">
        Journalists, researchers, professionals working in the system, and
        families — all welcome. The campaign grows by people speaking honestly.
      </p>
      <Link
        to="/contact"
        data-testid="campaign-contact-link"
        className="mt-8 inline-flex items-center gap-2 bg-ember text-ink px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:bg-ember/90 transition-colors"
      >
        Get in touch
      </Link>
    </div>
  );
}

export default function CampaignPage() {
  return (
    <div data-testid="campaign-page">
      <PageMeta
        title="Our campaign"
        description="Councils in England spent £15.5bn on children&rsquo;s social care in 2024–25. We campaign for a family court system that is open enough to be trusted."
        path="/campaign"
      />
      <PageHeader
        overline="Our campaign"
        title="A campaign for a more open child protection system."
        lede="Supporting families is half of why this site exists. The other half is a belief that the system itself needs to be far more open than it is."
      />
      <article className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <EssaySidebar />
          <div className="lg:col-span-9">
            <OpeningParas />
            <ScaleStat />
            <img
              src={HANDS_IMG}
              alt="Hands resting together"
              className="w-full h-[420px] md:h-[520px] object-cover my-12"
            />
            <div className="space-y-6 text-lg md:text-xl text-ink-muted leading-[1.8] font-light">
              <BeliefSection />
              <AsksList />
              <StandWithUs />
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
